import { Component, Inject, Prop, Vue, Watch } from 'vue-property-decorator';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import { ASK_DATA_SOURCES, ASK_TYPES, CHAT_SETTING, CONVERSATION_ROLE, LLM_MODELS, PAGES } from '@/shared/constants/ds-constants';
import { AskActivity, ConversationModel, PopoverReference, Resource, Sentence } from '@/shared/model/conversation.model';
import AskGooverService from '@/service/ask-goover.service';
import { PrincipalService } from '@/service/principal.service';

import { fetchEventSource } from '@microsoft/fetch-event-source';
import { namespace } from 'vuex-class';
import { formatDate, formatFromYYYYMMDDToMonthDate } from '@/util/date-util';
import { onErrorImageAvatar } from '@/util/common-util';

import moment from 'moment';
import { htmlToText } from 'html-to-text';
import BriefingService from '@/service/briefing.service';
import { extractContent, isNullOrEmptyString, removeUnUsedSpace } from '@/util/string.util';
import type { IBriefingPage, IResourceModel } from '@/shared/model/briefing-widget.model';
import { convertMarkdown2Html, getHostName, getValidURL } from '@/util/ds-util';
import { ASK_GOOVER_EVENT_TYPE, PAID_FEATURE_IDX, REFERENCE_STYLE_FOR, REFERENCE_TAB_SELECT, ROLE } from '@/constants';
import { AskMessageInfoRequest } from '@/shared/model/ask-goover.model';
import { checkIsGuestAccount } from '@/util/guest-util';
import { v4 as uuidv4 } from 'uuid';
import { checkLimitFeature, isPaidUser, isUltraUser } from '@/util/paid-user-util';
import NewReferenceCardComponent from '@/shared/cards/new-references/new-reference-card.vue';
import DiscoveryAndReportService from '@/service/discoveryAndReport.service';
import PopupSelectTypeReference from '@/entities/briefing/reference/popup-select-type-reference/popup-select-type-reference.vue';
import AlertDeepResearch from '@/v2/home/ask-goover/alert-deep-research/alert-deep-research.vue';
import { index } from 'd3';
import GenerateReportModal from '@/shared/modal/briefing/generate-report-modal/generate-report-modal.vue';
import { GoOverAgainBriefing } from '@/shared/model/briefing-widget.model';

const briefingStore = namespace('briefingStore');
const askGooverStore = namespace('askGooverStore');
const appStoreState = namespace('appStore');
const refPattern = /◖\d+◗/;

@Component({
  computed: {
    REFERENCE_STYLE_FOR() {
      return REFERENCE_STYLE_FOR;
    },
    REFERENCE_TAB_SELECT() {
      return REFERENCE_TAB_SELECT;
    },
    tooltipContent(): string {
      if (!this.indexName || this.indexName.length === 0) {
        return this.$t('cdHome.t19');
      }
      return this.indexName
        .map(item => {
          const found = ASK_DATA_SOURCES.find(dt => dt.value == item);
          return this.$t(found?.text);
        })
        .join(', ');
    },
  },
  methods: {
    convertMarkdown2Html,
    index,
    extractContent,
    onErrorImageAvatar,
    formatFromYYYYMMDDToMonthDate,
    isPaidUser,
  },
  components: {
    'vue-custom-scrollbar': vueCustomScrollbar,
    'popup-select-type-reference': PopupSelectTypeReference,
    'generate-report-modal': GenerateReportModal,
    'new-reference-card-component': NewReferenceCardComponent,
    'alert-deep-research': AlertDeepResearch,
  },
})
export default class AskConversation extends Vue {
  @Prop(Array) readonly referenceDocuments: Array<any> | [];
  @Prop(String) readonly page: string | '';
  @Prop(String) readonly targetBpId: string | '';
  @Prop(String) readonly docId: string | '';
  @Prop(String) readonly conversationId: string | '';
  @Prop(String) readonly stockCode: string | '';
  @Prop() readonly resourceIds: any;
  @Prop(String) readonly initQuestion: string | '';
  @Prop(Boolean) readonly showAddRefDropdown: boolean | false;
  @Prop(String) readonly briefingPageLanguage: string | 'en';
  @Prop(Boolean) readonly hasActivity: boolean | false;
  @Prop(Boolean) readonly isActive: boolean | null;
  @Prop(String) readonly bpType: string | '';
  @Prop(Boolean) readonly hideDatasourceOption: boolean;
  @Prop(Boolean) readonly hideResearchMode: boolean;
  @Prop(Boolean) readonly hideLlmModel: boolean;
  @Prop(Object) briefingPage: IBriefingPage;

  @Inject('askGooverService')
  private askGooverService: () => AskGooverService;

  @Inject('principalService')
  private principalService: () => PrincipalService;

  @Inject('briefingService')
  private briefingService: () => BriefingService;

  @Inject('discoveryService')
  public discoveryService: () => DiscoveryAndReportService;

  @briefingStore.Mutation
  public addToReferentDocuments!: (docIds: any[]) => void;

  @briefingStore.Getter
  public getSelectedBriefingPage!: IBriefingPage;

  @briefingStore.Getter
  public getIsOpenFromMyAskGoover!: boolean;

  @briefingStore.Mutation
  public setIsOpenFromMyAskGoover!: (payload: boolean) => void;

  @askGooverStore.Mutation
  public setConversationId!: (payload: any) => void;

  @appStoreState.Getter
  getCurrentUserImageUri!: string;

  @askGooverStore.Mutation
  getConversationId!: string;

  @briefingStore.Getter
  getReferenceDocuments!: Array<any>;

  @briefingStore.Getter
  public getSelectedResource!: IResourceModel;

  @askGooverStore.Getter
  getIsWaitingResponse!: boolean;

  @askGooverStore.Mutation
  setIsWaitingResponse!: (payload: any) => void;

  @appStoreState.Mutation
  public setIsShowReportSettingReportType!: (payload?: boolean) => void;

  @briefingStore.Mutation
  setSelectedReferenceTab!: (payload: string) => void;

  @appStoreState.Mutation
  public setShowAddReference!: (isShowAddReference: boolean) => void;

  @briefingStore.Getter
  getSelectedReferenceIds!: string[];

  @briefingStore.Mutation
  setReferenceDocuments!: (payload: any) => void;

  @appStoreState.Mutation
  public setShowCreateBriefing!: (isShowCreateBriefing: boolean) => void;

  @appStoreState.Getter
  getChatSetting!: any;

  @appStoreState.Mutation
  public setChatSetting!: (payload: any) => void;

  @appStoreState.Mutation
  public setIsCanShowCreateAgent!: (payload: boolean) => void;

  @appStoreState.Mutation
  public setIsOpenPopupEditReferenceForPage!: (payload: boolean) => void;

  @appStoreState.Mutation
  public setShowEditReportRef!: (payload: boolean) => void;

  @briefingStore.Mutation
  public setSelectedReferenceIds!: (payload: string[]) => void;

  @appStoreState.Mutation
  public setShowDetailReport!: (b: boolean) => void;

  @appStoreState.Mutation
  public setShowDetailTopic!: (b: boolean) => void;

  @appStoreState.Mutation
  public setShowDetailArticles!: (b: boolean) => void;

  @briefingStore.Mutation
  public setSelectedResource!: (payload: IResourceModel) => void;

  @briefingStore.Mutation
  public setIsCreateNewBriefingFromChat!: (IsCreateNewBriefingFromChat: boolean) => void;

  @briefingStore.Getter
  public getReferenceDocumentsForNewBriefing!: any[];

  @appStoreState.Getter
  getColorTheme!: string;

  @appStoreState.Mutation
  public setIsShowPopupRequiredLogin!: (payload: boolean) => void;

  @briefingStore.Mutation
  public setIsForNewBriefing!: (payload: boolean) => void;

  @briefingStore.Mutation
  public setIsLoadingBriefingWelcome!: (payload: boolean) => void;

  public CONVERSATION_ROLE = CONVERSATION_ROLE;
  public PAGES = PAGES;
  private conversation = new ConversationModel();
  private typingSentence = '';
  tempCurrentUserImageUri = this.getCurrentUserImageUri || 'content/images/avatar.png';

  isShowDetailActivity = false;
  suggestionList = [];
  isGenerateSuggestQuestion = false;
  waitingResponse = false;
  ASK_MANAGER_URL = process.env.ASK_MANAGER_URL;
  sseHeader = {
    Accept: 'text/event-stream',
    'Content-Type': 'application/stream+json',
    Authorization: 'Bearer ' + (localStorage.getItem('ds-authenticationToken') || sessionStorage.getItem('ds-authenticationToken')),
    // Authorization:
    //   'Bearer eyJhbGciOiJIUzUxMiJ9.eyJqdGkiOiIyY2M1ODRmZC03MzFmLTQ1NjctOTA2MC1mMjNlYzdlNmRkOGQiLCJpc3MiOiJEZWVwc2lnbmFsIiwiYXVkIjoiRGVlcHNpZ25hbCIsImlhdCI6MTc1MDc0MDI5MSwic3ViIjoibG9uZ3Z1b25nMTMwN0BnbWFpbC5jb20iLCJhdXRoIjoiUk9MRV9QQUlEX1VMVFJBLFJPTEVfVVNFUiIsInJlZnJlc2hDb3VudCI6MTQsInJlZnJlc2hMaW1pdCI6MTUsImV4cCI6MTc1MzMzMjI5MX0.k5f7piz1ytCn0P0V9Cq4R0FGPppRtsAX6SQkJOUS-o0uKjusZaZl06JrQpw7DHYr4WVdhRDNd1VNvC_DQegi2g',
  };
  private ASK_ACTION = {
    CHAT: 'CHAT',
    DIVE_DEEPER: 'DIVE_DEEPER',
    PAYLOAD: '',
  };
  private lastAskAction;
  private listCopiedAnswerIndex = [];
  private sessionId;
  private cancelStream = false;
  ragDocumentsIds = [];
  idDetected = new Date().getTime().toString();
  tempUser = JSON.parse(localStorage.getItem('localStorageUser')) || {};
  buttonLikeOrDisLikeKey = new Date().getTime().toString() || '';
  popoverDataList: PopoverReference[] = [];
  isHaveChatResearchData = false;
  askActivities: AskActivity[] = [];
  researchType = 'quick';
  llmType = 'none';
  indexName = ['BASIC'];
  titleChat = '';
  isEditing = false;
  checkedReferenceIds = [];
  tempTitle = '';
  currentConversation: Sentence;
  streamLLMModel: string;

  openSelectTypeReference = false;

  @briefingStore.Getter
  public getIsAllowGoOverAgain!: boolean;

  @briefingStore.Mutation
  public setIsAllowGoOverAgain!: (payload: boolean) => void;

  isShowAlert = false;
  askTypes = ASK_TYPES;
  llmModels = LLM_MODELS;
  dataSources = ASK_DATA_SOURCES;
  shouldAutoScroll = true;
  lastScrollTop = 0;

  @Watch('$route.fullPath')
  onRouteChange() {
    this.checkedReferenceIds = [];
    this.setSelectedReferenceIds([]);
    this.setIsOpenPopupEditReferenceForPage(false);
    const q = this.$route?.query?.q as string;
    if (q) {
      this.typingSentence = q;
      this.sendSentence();
    }
  }

  @Watch('getCurrentUserImageUri')
  onCurrentUserImageUriChange() {
    if (this.getCurrentUserImageUri) {
      this.tempCurrentUserImageUri = this.getCurrentUserImageUri;
    } else {
      this.tempCurrentUserImageUri = 'content/images/avatar.png';
    }
  }

  @Watch('targetBpId')
  onTargetBpIdChange() {
    if (this.page === PAGES.BRIEFING || this.page === PAGES.SOCIAL_BRIEFING) {
      this.initConversationHistoryTarget();
    }
  }

  @Watch('docId')
  onDocIdChange() {
    if (this.page === PAGES.DISCOVERY_DETAIL || this.page === PAGES.REPORT_DETAIL) {
      this.initConversationWithDocument();
    }
  }

  @Watch('stockCode')
  onStockCodeChange() {
    this.initCompanyBriefingWelcome();
  }

  created() {
    // this.checkActiveTabListener();
    // const fromPath = this.$route.meta.fromPath;
    // const queryQuestion = this.$route?.query?.q as string;
    // if (fromPath !== '/home' && !fromPath.includes('/home?t=') && queryQuestion) {
    //   const conversationId = localStorage.getItem('conversationId');
    //   this.initConversationHistoryTarget(conversationId);
    //   return;
    // }
    if ((this.page === 'detail' && !this.targetBpId) || (this.page === 'report' && !this.targetBpId)) {
      this.initConversationWithDocument();
    } else if (
      this.page === PAGES.BRIEFING ||
      this.page == PAGES.SOCIAL_BRIEFING ||
      this.page == PAGES.REPORT_DETAIL ||
      (this.page === 'detail' && this.targetBpId)
    ) {
      this.initConversationHistoryTarget();
      return;
    } else if (this.page === PAGES.COMPANY_BRIEFING) {
      this.initCompanyBriefingWelcome();
    } else if (this.page === PAGES.COMPANY_BRIEFING_WIDGET) {
      this.initCompanyBriefingWidgetSummarize();
    }
    setTimeout(() => {
      if (this.initQuestion && !this.$route?.query?.conversationId) {
        this.typingSentence = this.initQuestion;
        this.titleChat = this.initQuestion;
        this.sendSentence();
      }
    }, 100);
    // this.getSuggestQuestions();
  }

  mounted() {
    // if (location.href.indexOf('dev-') > -1 || location.href.indexOf('localhost:') > -1) {
    //   this.ASK_MANAGER_URL = 'https://dev-chat.goover.ai:7731/askgoover';
    // }
    this.sessionId = this.$route.query?.t || '';
    this.tempCurrentUserImageUri = this.getCurrentUserImageUri || 'content/images/avatar.png';
    this.ragDocumentsIds = [];

    this.researchType = this.getChatSetting.researchMode || 'quick';
    this.llmType = this.getChatSetting.llmModel || 'none';
    this.indexName = this.getChatSetting?.dataSources || ['WEB'];
    if (this.conversationId) {
      this.showOldChatHistory(this.conversationId);
    }
    if (this.tempUser) {
      this.isShowAlert =
        this.tempUser.authorities.includes(ROLE.ROLE_PAID_PRO) && !this.tempUser.authorities.includes(ROLE.ROLE_PAID_ULTRA);
    }

    // @ts-ignore
    const el = this.$refs['go-list-inner']?.$el;
    if (el) {
      el.addEventListener('scroll', this.handleUserScroll, { passive: true });
    }
  }

  hasActivityLoading() {
    return this.askActivities.some(activity => activity.isLoading);
  }

  activated() {
    this.cancelStream = false;
  }

  deactivated() {
    this.cancelStream = true;
    this.ragDocumentsIds = [];
    this.openSelectTypeReference = false;
    this.setIsWaitingResponse(false);
  }

  @Watch('referenceDocuments', { immediate: true })
  onReferenceDocumentsChange() {
    if (this.page === PAGES.BRIEFING) {
      if (this.$route.name === 'MyBriefing-Detail') {
        if (this.checkIfExistedDataInLocalStorage()) {
          const isAllowGoOverAgain = !this.compareReferencesBetweenLocalStorageAndState();
          this.setIsAllowGoOverAgain(isAllowGoOverAgain);
          return;
        } else {
          if (!this.referenceDocuments.length || this.referenceDocuments.some(el => el.bpId !== this.targetBpId)) {
            const isAllowGoOverAgain = false;
            this.setIsAllowGoOverAgain(isAllowGoOverAgain);
            return;
          }
          const tempData: GoOverAgainBriefing = {
            bpId: this.targetBpId,
            referenceDocIds: this.referenceDocuments.map(el => el.docId || el.parentDocId),
          };
          this.addBriefingGoOverAgainDataToLocalStorage(tempData);
          const isAllowGoOverAgain = false;
          this.setIsAllowGoOverAgain(isAllowGoOverAgain);
          return;
        }
      }
    }
  }

  addBriefingGoOverAgainDataToLocalStorage(dataInput: GoOverAgainBriefing) {
    if (this.getLocalStorageBriefingGoOverAgainData()?.length > 0) {
      const parsedData: GoOverAgainBriefing[] = JSON.parse(this.getLocalStorageBriefingGoOverAgainData());

      const filtered = parsedData.filter(el => el.bpId !== dataInput.bpId);

      if (filtered?.length > 0) {
        this.setLocalStorageBriefingGoOverAgainData([...filtered, dataInput]);
      } else {
        this.setLocalStorageBriefingGoOverAgainData([dataInput]);
      }
    } else {
      this.setLocalStorageBriefingGoOverAgainData([dataInput]);
    }
  }

  setLocalStorageBriefingGoOverAgainData(listDataInput: any[]) {
    if (listDataInput?.length > 0) {
      localStorage.setItem('localStorageBriefingGoOverAgainData', JSON.stringify(listDataInput));
    } else {
      localStorage.setItem('localStorageBriefingGoOverAgainData', '');
    }
  }

  getLocalStorageBriefingGoOverAgainData() {
    return localStorage.getItem('localStorageBriefingGoOverAgainData') || '';
  }

  compareReferencesBetweenLocalStorageAndState() {
    if (!this.targetBpId) return true;
    const localStorageBriefingGoOverAgainData = this.getLocalStorageBriefingGoOverAgainData();
    if (!localStorageBriefingGoOverAgainData) return true;
    const parsedData: GoOverAgainBriefing[] = JSON.parse(localStorageBriefingGoOverAgainData) || [];
    const itemMatch = parsedData.find(el => el.bpId === this.targetBpId);
    if (!itemMatch) return true;
    if (itemMatch.referenceDocIds.length !== this.referenceDocuments.length) return false;
    const referenceDocIds = this.referenceDocuments.map(el => el.docId || el.parentDocId);
    return itemMatch.referenceDocIds.every(docId => referenceDocIds.includes(docId));
  }

  checkIfExistedDataInLocalStorage() {
    let isExisted = false;

    if (this.getLocalStorageBriefingGoOverAgainData() == '') {
      return false;
    } else {
      const parsedData: GoOverAgainBriefing[] = JSON.parse(this.getLocalStorageBriefingGoOverAgainData());

      if (parsedData?.length > 0) {
        parsedData.forEach(item => {
          if (item?.bpId === this.targetBpId) {
            isExisted = true;
            return true;
          }
        });
      } else {
        return false;
      }
    }

    return isExisted;
  }

  get shouldDisableChat(): boolean {
    return this.isActive === false && this.$route.name === 'MyBriefing-Detail' && this.bpType !== 'RECOMMENDATION_BRIEFING';
  }

  showOldChatHistory(conversationId: string) {
    localStorage.setItem('conversationId', conversationId);
    this.setConversationId(conversationId);
    // clear all message in chat
    this.conversation.sentences.splice(0, this.conversation.sentences.length);
    this.initConversationHistoryTarget(conversationId);
  }

  chooseSuggestSentence(sentence: string) {
    this.typingSentence = sentence;
    this.suggestionList.splice(0, this.suggestionList.length);
    this.sendSentence();
  }

  async sendSentence(researchType?: string, llmType?: string) {
    const tempText = removeUnUsedSpace(this.typingSentence || '', true) || '';

    if (tempText !== undefined && tempText !== null && tempText?.trim()?.length > 0) {
      this.typingSentence = tempText;
    } else {
      return;
    }

    this.shouldAutoScroll = true;
    this.lastScrollTop = 0;
    const userSentence = this.typingSentence;
    this.addQuestionToConversation();
    this.waitingResponse = true;
    this.lastAskAction = this.ASK_ACTION.CHAT;
    this.ASK_ACTION.PAYLOAD = userSentence;
    let conversationId;
    this.isHaveChatResearchData = false;

    this.tempUser = JSON.parse(localStorage.getItem('localStorageUser')) || {};

    const languageForAsk =
      (this.tempUser !== undefined && this.tempUser?.langKey !== undefined && this.tempUser?.langKey?.trim()?.length > 0
        ? this.tempUser?.langKey?.toUpperCase()
        : this.$store.getters?.userIdentity?.langKey?.toUpperCase() || this.briefingPageLanguage
        ? this.briefingPageLanguage.toUpperCase()
        : 'EN') || 'EN';

    let page;
    if (this.page == PAGES.COMPANY_BRIEFING_WIDGET) {
      page = PAGES.COMPANY_BRIEFING;
    } else {
      if (this.page == PAGES.SOCIAL_BRIEFING) {
        page = PAGES.BRIEFING;
      } else {
        page = this.page;
      }
    }
    try {
      if (!this.conversation?.conversationId || this.page == PAGES.SOCIAL_BRIEFING) {
        conversationId = await this.askGooverService().generateConversationId(
          this.$store.getters?.userIdentity?.id,
          languageForAsk,
          page,
          this.$route.params.bpId,
          '',
          '',
          'CC' + Date.now().toString(),
          this.page === PAGES.COMPANY_BRIEFING || PAGES.COMPANY_BRIEFING_WIDGET ? this.stockCode : undefined
        );

        this.conversation.conversationId = conversationId.data;
        localStorage.setItem('conversationId', conversationId.data);
        this.setConversationId(conversationId.data);
      }
      // }
    } catch (err) {
      this.waitingResponse = false;
      return;
    }

    const requestId = Date.now().toString();
    const requestBody = this.buildRequestBody();
    requestBody.requestId = requestId;
    requestBody.conversationId = this.conversation.conversationId;
    const { contentLanguages, langKey } = this.tempUser;
    let language = ['EN'];
    if (this.briefingPageLanguage) {
      language = [this.briefingPageLanguage.toUpperCase()];
    } else if (contentLanguages.length > 0) {
      language = contentLanguages.map(item => item.toUpperCase());
    } else if (langKey) {
      language = [langKey.toUpperCase()];
    }
    requestBody.language = language;
    requestBody.userKey = this.$store.getters?.userIdentity?.id;
    requestBody.message = userSentence;
    requestBody.page = page;
    requestBody.researchType = researchType !== undefined ? researchType : this.researchType;
    requestBody.llmType = llmType !== undefined ? llmType : this.llmType;

    const responseMessage = '';
    const resources: Array<Resource> = [];
    this.suggestionList = [];

    this.callSSEApi(
      this.ASK_MANAGER_URL + '/chat/v2',
      requestBody,
      responseMessage,
      resources,
      requestId,
      requestBody.researchType == 'deep'
    );
  }

  resize() {
    const textarea = this.$refs.chatInput as any;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  }

  countTimeAutoExpandPanel = 0;
  async callSSEApi(apiEndpoint, requestBody, responseMessage, resources, requestId, isDeepAnswer?) {
    console.log('qq = ', this.getReferenceDocuments, this.checkedReferenceIds);
    try {
      const tempResources = [];
      this.popoverDataList = [];
      // this.answerGenerationProcessData = [];

      // requestBody.researchType = this.researchType;
      // requestBody.llmType = this.llmType;
      requestBody.indexName = this.indexName;
      requestBody.userTime = new Date();

      let isNeedReference = true;
      if (this.page == PAGES.BRIEFING && this.briefingPage?.userProfile?.userKey != this.tempUser.id) {
        isNeedReference = false;
      }

      const that = this;
      const callbackSessionId = this.sessionId;
      let currentMessageId = '';
      await this.askGooverService().saveLogChat(requestBody);
      await fetchEventSource(apiEndpoint, {
        method: 'POST',
        headers: this.sseHeader,
        body: JSON.stringify(requestBody),
        onmessage(event) {
          if ((that.$route.name === 'Home' && that.sessionId !== callbackSessionId) || that.cancelStream) {
            that.cancelStream = false;
            const error = new Error();
            error.name = 'ChangePage';
            throw error;
          }

          switch (event.event) {
            case ASK_GOOVER_EVENT_TYPE.ACTIVITY_CONTENT.toString():
              if (event.data) {
                const tempAnswerGenerationProcessStart: AskActivity = {
                  content: JSON.parse(event.data).content,
                  isLoading: true,
                  activityId: '',
                  description: '',
                  id: JSON.parse(event.data)?.activity_id,
                };
                // update loading previous to done before push
                if (that.askActivities.length > 0) {
                  that.askActivities.map(item => {
                    item.isLoading = false;
                    return item;
                  });
                  if (requestBody.researchType == 'deep' && !that.isShowDetailActivity && that.countTimeAutoExpandPanel == 0) {
                    that.countTimeAutoExpandPanel++;
                    that.isShowDetailActivity = true;
                  }
                }
                that.askActivities.push(tempAnswerGenerationProcessStart);
              }

              break;

            case ASK_GOOVER_EVENT_TYPE.ANSWER_STREAM_END.toString():
              if (event.data && !JSON.parse(event.data).content?.includes(ASK_GOOVER_EVENT_TYPE.ANSWER_STREAM_END)) {
                currentMessageId = JSON.parse(event.data).content;
              }
              responseMessage = '';
              break;

            case ASK_GOOVER_EVENT_TYPE.ACTIVITY_STREAM.toString():
              if (event.data) {
                const content = JSON.parse(event.data).content;
                const id = JSON.parse(event.data).activity_id;
                that.addContentToActivity(content, id);
              }
              break;
            case ASK_GOOVER_EVENT_TYPE.ANSWER_STREAM_START.toString():
              if (event.data) {
                that.isShowDetailActivity = false;
                that.streamLLMModel = JSON.parse(event.data).llm_model;
                if (!that.currentConversation) {
                  that.currentConversation = new Sentence(
                    CONVERSATION_ROLE.ASSISTANT,
                    responseMessage,
                    Date.now(),
                    resources.map(resource => resource.doc_id),
                    currentMessageId,
                    isDeepAnswer,
                    that.streamLLMModel
                  );
                  that.addToConversationSentences(that.currentConversation, true);
                } else {
                  that.currentConversation.ipUpdateConversation = true;
                  // that.currentConversation.content += responseMessage;
                  that.currentConversation.llmModel = that.streamLLMModel;
                  that.addToConversationSentences(that.currentConversation, true);
                }
              }
              break;
            case ASK_GOOVER_EVENT_TYPE.REFERENCE_DOCUMENT.toString():
              resources.splice(0, resources.length - 1);
              // start of stream
              if (event?.data) {
                const dataJSON = JSON.parse(event?.data).content;
                dataJSON.forEach(ragDoc => {
                  if (!ragDoc.doc_id || !ragDoc.title || !ragDoc.url) return;

                  tempResources.push(ragDoc);

                  // @ts-ignore
                  resources.push(
                    new Resource(
                      ragDoc?.doc_id,
                      ragDoc?.title,
                      ragDoc?.url,
                      ragDoc?.published_at,
                      ragDoc?.content,
                      ragDoc?.chunk_index,
                      ragDoc?.chunk_id
                    )
                  );
                });

                that.conversation.resourceIds.push(...resources);
                that.addToReferenceDocuments(resources);
                that.updateToTrueIndexNumber();
              }

              break;

            case ASK_GOOVER_EVENT_TYPE.ANSWER_STREAM.toString(): {
              responseMessage = responseMessage + JSON.parse(event.data)?.content;

              if (refPattern.test(responseMessage)) {
                if (tempResources?.length > 0) {
                  responseMessage = responseMessage.replace(/◖\d+◗/g, match => {
                    if (isNeedReference) {
                      const tempValue = match.replace(/[◖◗]/g, '') || '';
                      const result = that.makeDataPopover(tempValue, tempResources) || null;

                      if (result) {
                        const { idElement, numRef, url } = result;

                        return `<a class="go-btn-area" href="${url}" target="_blank"><button id="${idElement}" class="btn go-num-doc">${numRef}</button></a>`;
                      } else {
                        return `<a class="go-btn-area" target="_blank"><button class="btn go-num-doc">${tempValue}</button></a>`;
                      }
                    } else {
                      return '';
                    }
                  });
                }
              }
              // @ts-ignore
              const lastestAnswerIndex = that.conversation?.sentences
                .map(sentence => sentence.role)
                .lastIndexOf(CONVERSATION_ROLE.ASSISTANT);

              if (lastestAnswerIndex > -1) {
                // find current LLM on stream
                const foundIndex = that.conversation?.sentences?.[lastestAnswerIndex]?.llmContent.findIndex(
                  item => item.model == that.streamLLMModel
                );
                // @ts-ignore
                that.conversation?.sentences?.[lastestAnswerIndex]?.llmContent[foundIndex || 0].content = responseMessage;
              }
              break;
            }

            case ASK_GOOVER_EVENT_TYPE.SUGGEST.toString():
              if (event.data) {
                // @ts-ignore
                that.suggestionList = JSON.parse(event.data).content;
                that.doneChatResponse();
              }
              break;
          }

          that.conversation?.sentences.forEach(sentence => {
            sentence.messageId = currentMessageId;
          });
          that.scrollToBottom();
          return;
        },
        onerror(err) {
          that.waitingResponse = false;
          that.cancelStream = false;
          const errorSentence = new Sentence(CONVERSATION_ROLE.ASSISTANT, undefined, Date.now());
          errorSentence.llmContent = null;
          that.addToConversationSentences(errorSentence);
          throw err;
        },
        openWhenHidden: true,
      });
    } finally {
      this.doneChatResponse();
    }
  }

  backToHome() {
    this.$emit('moveTo', { initQuestion: '', isConversation: false });
    this.setChatSetting(CHAT_SETTING);
    this.$router.replace({ path: 'home', query: null });
    this.checkedReferenceIds = [];
    this.setSelectedReferenceIds([]);
  }

  addToReferenceDocuments(resources: Resource[]) {
    if (!resources.length || this.page === 'detail' || this.page === 'report' || this.$route.name === 'SocialBriefing-Detail') {
      return;
    }

    // if (
    //   this.page !== undefined &&
    //   (this.page === PAGES.HOME || this.page === PAGES.BRIEFING)
    // ) {
    //   return;
    // }

    let lastIndex = this.referenceDocuments.length
      ? this.referenceDocuments[this.referenceDocuments.length - 1]?.referenceDocumentIndex
      : 0;

    let maxReferenceDocumentIndex = this.referenceDocuments
      ? Math.max(...this.referenceDocuments?.map(item => item.referenceDocumentIndex))
      : 0 || 0;

    maxReferenceDocumentIndex = maxReferenceDocumentIndex > -1 ? maxReferenceDocumentIndex : 0;

    const newResource = resources.filter(resource => !this.checkDuplicateResource(resource));
    newResource.forEach(resource => {
      lastIndex++;
      maxReferenceDocumentIndex++;
      Object.assign(resource, {
        referenceDocumentIndex: maxReferenceDocumentIndex ? maxReferenceDocumentIndex : lastIndex,
        requestId: 'Ask-' + Date.now().toString(),
        parentDocId: resource?.doc_id ? resource?.doc_id : '',
        parent_doc_id: resource?.doc_id ? [resource?.doc_id] : [],
        language: this.$store.getters?.userIdentity?.langKey.toLowerCase() || 'en',
        userKey: this.$store.getters?.userIdentity?.id ? this.$store.getters?.userIdentity?.id : '',
        requestedResourceList: newResource.map(resource => resource.doc_id),
        forcedClustering: false,
        targetBpId: this.targetBpId ? this.targetBpId : '',
        path: resource?.url ? resource?.url : resource?.path ? resource?.path : '',
        type: 'WEB',
        searchType: 'WEB',
        // docId: resource?.doc_id || '',
        // searchType: ['WEB'],
        // author: resource?.url,
        writer: resource?.writer
          ? resource?.writer
          : resource?.url
          ? [getHostName(resource?.url) ? getHostName(resource?.url) : '']
          : resource?.path
          ? [getHostName(resource?.path) ? getHostName(resource?.path) : '']
          : [''],
        sourceUriDomain: getValidURL(resource?.url)?.host || resource.url,
        description: resource?.content ? resource?.content : '',
        favicon: '',
        keyword: '',
        originDate: resource.published_at ? resource.published_at : '',
        image_uri: [''],
        image_uri_thumbnail: '',
        mediaType: ['WEB'],
        docType: 1,
        isInsideGroup: false,
        isGroup: false,
        published_at: '',
        service_html: '',
        chunkText: resource?.content ? resource?.content : '',
        owner_user_key: resource?.owner_user_key
          ? resource?.owner_user_key
          : resource?.originParentDocDetail?.owner_user_key
          ? resource?.originParentDocDetail?.owner_user_key
          : null,
        channel: resource?.url
          ? getHostName(resource?.url)
          : resource?.path
          ? getHostName(resource?.path)
          : resource?.writer?.[0]
          ? resource?.writer?.[0]
          : '',
      });
    });
    // this.briefingService().saveBriefingResource(newResource);

    // add parameter isUpdate = true in briefing detail
    if (this.$route.name === 'MyBriefing-Detail' || (this.page !== undefined && this.page === PAGES.BRIEFING)) {
      this.briefingService().saveBriefingResource(newResource, true);
    } else {
      this.briefingService().saveBriefingResource(newResource);
    }

    this.addToReferentDocuments(newResource);

    const tempList: any[] = newResource || [];

    if (tempList && tempList.length > 0) {
      if (this.ragDocumentsIds && this.ragDocumentsIds.length > 0) {
        this.ragDocumentsIds = [
          ...this.ragDocumentsIds,
          ...tempList.map(item => (item.parentDocId ? item.parentDocId : item?.parent_doc_id ? item?.parent_doc_id[0] : item.doc_id)),
        ];
      } else {
        this.ragDocumentsIds = tempList.map(item =>
          item.parentDocId ? item.parentDocId : item?.parent_doc_id ? item?.parent_doc_id[0] : item.doc_id
        );
      }
    }
  }

  initConversationHistoryTarget(conversationId?: string) {
    if (!conversationId || conversationId.length === 0) {
      if (!this.targetBpId) {
        return;
      }
      let currentConversationId = this.tempUser.id + ':' + this.targetBpId;
      if (this.getConversationId === currentConversationId) return;
      if (!this.targetBpId) {
        currentConversationId = this.$store.getters?.userIdentity?.id + ':' + this.$route.params.bpId;
      }
      conversationId = currentConversationId;
    }

    let isSetTitle = false;

    this.conversation.conversationId = conversationId;
    localStorage.setItem('conversationId', conversationId);
    this.setConversationId(conversationId);
    this.waitingResponse = true;
    this.askActivities = [];
    this.askGooverService()
      .getConversationById(conversationId)
      .then(async res => {
        if (res.data?.history?.length) {
          if (
            !(
              this.page === 'detail' ||
              this.page === 'report' ||
              this.$route.name === 'SocialBriefing-Detail' ||
              this.$route.name === 'MyBriefing-Detail'
            )
          ) {
            this.setReferenceDocuments([]);

            if (res.data?.resourceIds?.length > 0) {
              const tempList = res.data?.resourceIds.filter(item => item && item !== 'null' && item?.trim()?.length > 0);

              if (tempList.length > 0) {
                await this.getListContentDetailByIds(res.data?.resourceIds, 'getRAG');
              }
            }
          }

          const ragList = [];
          for (let i = 0; i < res.data.history.length; i++) {
            res.data.history[i]?.messages.forEach(message => {
              if (!isSetTitle && message.role === CONVERSATION_ROLE.USER) {
                this.titleChat = res.data.title ? res.data.title : message.content;
                isSetTitle = true;
              }

              const sentence = new Sentence(
                message.role,
                message.content,
                message.messageDate,
                message.ragList,
                message.messageId,
                message.isDeepAnswer,
                null,
                message.like
              );

              if (message.role === CONVERSATION_ROLE.ASSISTANT) {
                if (message.llmContent) {
                  for (const eachContent of message.llmContent) {
                    if (refPattern.test(eachContent.content)) {
                      eachContent.content = eachContent.content.replace(/◖\d+◗/g, match => {
                        const tempValue = match.replace(/[◖◗]/g, '') || '';
                        const { idElement, url, numRef } = this.makeDataPopover(tempValue, message.ragList);
                        return `<a class="go-btn-area" target="_blank" href="${url}"><button id="${idElement}" class="btn go-num-doc">${numRef}</button></a>`;
                      });
                    }
                  }
                  sentence.llmContent = message.llmContent.filter(item => item.content);
                } else if (message.content) {
                  if (refPattern.test(message.content)) {
                    message.content = message.content.replace(/◖\d+◗/g, match => {
                      const tempValue = match.replace(/[◖◗]/g, '') || '';
                      const { idElement, url, numRef } = this.makeDataPopover(tempValue, message.ragList);
                      return `<a class="go-btn-area" target="_blank" href="${url}"><button id="${idElement}" class="btn go-num-doc">${numRef}</button></a>`;
                    });
                  }
                  sentence.llmContent = [{ model: '', content: message.content }];
                }
              }

              const { activity } = message;
              if (activity) {
                this.askActivities = this.processActivities(activity);
              }

              // this.addToConversationSentences(sentence);
              this.conversation.sentences.push(sentence);
              ragList.push(...message.ragList);
            });
          }

          this.shouldAutoScroll = true;
          this.scrollToBottom();
          if (this.page === PAGES.REPORT_DETAIL) {
            this.initConversationWithDocument();
          } else {
            this.getSuggestQuestions(
              conversationId,
              ragList
                .map(item => item?.docId)
                .reverse()
                .slice(0, 5)
            );
          }
        } else {
          if (this.page === PAGES.REPORT_DETAIL) {
            this.initConversationWithDocument();
          } else {
            this.getSuggestQuestions(conversationId, this.resourceIds);
          }
        }
        this.waitingResponse = false;
      });
  }

  processActivities(activities) {
    const seen = {};
    const result = [];

    for (let i = 0; i < activities.length; i++) {
      const activity = activities[i];
      const id = activity.activityId;

      if (activity.event === 'depth2') {
        // Store depth2 content for later
        if (!seen[id]) {
          seen[id] = { index: -1, depth2Content: activity.content };
        } else {
          seen[id].depth2Content = activity.content;
        }
        continue; // Skip adding depth2 to result
      }

      // depth1 or other events
      const index = result.length;
      result.push({ ...activity });

      if (!seen[id]) {
        seen[id] = { index, depth2Content: null };
      }
    }

    // Inject description into first activity if matching depth2 content was found
    // @ts-ignore
    for (const { index, depth2Content } of Object.values(seen)) {
      if (depth2Content !== null && index !== -1) {
        result[index].description = depth2Content;
      }
    }

    return result;
  }

  initConversationWithDocument() {
    console.log('called initConversationWithDocument');
    if (!this.docId) return;
    this.askGooverService()
      .generateConversationId(
        this.$store.getters?.userIdentity?.id,
        this.$store.getters?.userIdentity?.langKey.toLowerCase() || 'en',
        this.page,
        undefined,
        this.docId,
        undefined,
        'CC-Doc-' + Date.now().toString()
      )
      .then(res => {
        this.conversation.conversationId = res.data;
        this.waitingResponse = true;
        this.askGooverService()
          .summaryAndSuggestQuestions(
            this.conversation.conversationId,
            this.docId,
            this.$store.getters?.userIdentity?.langKey.toUpperCase() || 'EN'
          )
          .then(res => {
            if (res.data) {
              if (this.conversation.sentences?.length == 0) {
                this.conversation.sentences.push(new Sentence(CONVERSATION_ROLE.ASSISTANT, res.data?.summary, Date.now().toString()));
              }
              this.suggestionList = res.data?.suggests;
              this.scrollToBottom();
            }
          })
          .finally(() => {
            this.waitingResponse = false;
          });
      });
  }

  getSuggestQuestions(conversationId?, resourceIds?) {
    if (this.isGenerateSuggestQuestion) {
      return;
    }

    const requestBody = new ConversationModel();
    requestBody['requestId'] = Date.now().toString();
    requestBody.conversationId = conversationId ? conversationId : '';
    requestBody.language = this.briefingPageLanguage ? this.briefingPageLanguage.toLowerCase() : this.$i18n?.locale?.toLowerCase();
    requestBody.resourceIds = resourceIds ? resourceIds : [];
    requestBody.userKey = this.principalService().getUserInfo()?.id || '';
    requestBody.briefingId = this.targetBpId || '';
    requestBody.page = this.page == PAGES.SOCIAL_BRIEFING ? PAGES.BRIEFING : this.page;
    delete requestBody.sentences;

    this.isGenerateSuggestQuestion = true;

    this.askGooverService()
      .getSuggestQuestions(requestBody)
      .then(res => {
        if (res?.data?.body) {
          this.suggestionList = res?.data?.body;
          this.scrollToBottom();
        }
      })
      .finally(() => {
        this.isGenerateSuggestQuestion = false;
      });
  }

  addToConversationSentences(sentence: Sentence, isFromTarget?: boolean) {
    let isPushNewSentence = true;

    if (isFromTarget !== undefined && isFromTarget) {
      if (this.isHaveChatResearchData) {
        isPushNewSentence = false;
      }
    }

    if (isPushNewSentence) {
      if (sentence.ipUpdateConversation) {
        this.conversation.sentences[this.conversation.sentences.length - 1].llmContent.push({
          model: sentence.llmModel,
          content: sentence.content,
        });
      } else {
        this.conversation?.sentences.push(sentence);
      }
    } else {
      if (this.conversation?.sentences?.length > 0) {
        this.conversation?.sentences.pop();
      }

      this.conversation?.sentences.push(sentence);
    }

    this.suggestionList.splice(0, this.suggestionList.length);
  }

  addQuestionToConversation() {
    const sentence = new Sentence(
      this.CONVERSATION_ROLE.USER,
      // @ts-ignore
      this.typingSentence.replaceAll('\n', '<br />'),
      new Date()
    );
    if (!this.titleChat) {
      this.titleChat = this.typingSentence;
    }

    if (this.typingSentence.length) {
      this.conversation?.sentences.push(sentence);
    }
    this.typingSentence = '';
    this.scrollToBottom();
  }

  resetChat() {
    this.typingSentence = '';
    this.conversation = new ConversationModel();
    this.suggestionList.splice(0, this.suggestionList.length);
    this.sessionId = this.$route.query?.t || '';
    this.ragDocumentsIds = [];
    this.currentConversation = null;
  }

  scrollToBottom() {
    this.scrollToBottomAnswerGenerationProcessData();
    if (!this.shouldAutoScroll) return;

    // @ts-ignore
    this.$refs['go-list-inner']?.$el?.scrollTop = this.$refs['go-chat-list']?.clientHeight + 200 || 0;
  }

  handleUserScroll() {
    // @ts-ignore
    const container = this.$refs['go-list-inner']?.$el;
    if (!container) return;

    const currentScrollTop = container.scrollTop;

    // Scroll up
    if (currentScrollTop < this.lastScrollTop) {
      this.shouldAutoScroll = false;
    }
    this.lastScrollTop = currentScrollTop;
  }

  resendDeepResearch(indexInput: number) {
    this.conversation.sentences[indexInput - 1].isDeepAnswer = false;
    this.typingSentence = this.conversation.sentences[indexInput - 1].content;
    let llmType;
    if (
      this.conversation.sentences[indexInput].llmModel == 'goover' ||
      this.conversation.sentences[indexInput].llmContent[0].model == 'goover'
    ) {
      llmType = 'none';
    } else {
      llmType = this.llmModels.find(item => item.value == this.conversation.sentences[indexInput].llmContent[0].model).value;
    }
    this.sendSentence('deep', llmType);
    this.conversation.sentences[indexInput - 1].isDeepAnswer = true;
  }

  reloadAskAction() {
    if (this.ASK_ACTION.CHAT === this.lastAskAction) {
      this.typingSentence = this.ASK_ACTION.PAYLOAD;
      this.sendSentence();
    }
  }

  indexOfResourceInBriefingReference(resource) {
    return this.referenceDocuments.findIndex(refDoc => refDoc.doc_id === resource || refDoc.docId === resource);
  }

  checkDuplicateResource(resource) {
    return this.referenceDocuments.some(
      referenceDocument => referenceDocument.path === resource.url || referenceDocument.parentDocId === resource.doc_id
    );
  }

  doneChatResponse() {
    this.waitingResponse = false;
    this.currentConversation = null;
    this.streamLLMModel = null;
    this.askActivities = this.askActivities.map(item => {
      item.isLoading = false;
      return item;
    });

    this.$nextTick(() => {
      // @ts-ignore
      this.$refs['chatInput']?.focus();
    });
  }

  copyText(indexSentence: number, content: any) {
    const element = document.getElementById('go-block-body_' + indexSentence);
    let rawHtml = content;
    let text = content;
    if (element) {
      rawHtml = element.innerHTML;
      try {
        text = this.removeAnchorButtons(rawHtml);
      } catch (e) {
        text = rawHtml;
      }
    }

    navigator.clipboard.writeText(htmlToText(text));
    if (this.listCopiedAnswerIndex.indexOf(indexSentence) === -1) {
      this.listCopiedAnswerIndex.push(indexSentence);
    }
    setTimeout(() => {
      const index = this.listCopiedAnswerIndex.indexOf(indexSentence);
      if (index > -1) {
        this.listCopiedAnswerIndex.splice(index, 1);
      }
    }, 5000);
  }

  handleUserInteract(sentence, likeInput?: number, indexInput?: number, event?: any) {
    if (checkIsGuestAccount(true)) {
      event?.stopPropagation();
      event?.preventDefault();

      return;
    }

    let likeStatusNumber = 0;
    const tempLikeStatus = indexInput && indexInput > -1 ? this.conversation?.sentences?.[indexInput]?.like || 0 : 0;

    if (likeInput && likeInput === 1) {
      switch (tempLikeStatus) {
        case 1:
          likeStatusNumber = 0;
          break;
        case -1:
          likeStatusNumber = 1;
          break;
        default:
          likeStatusNumber = 1;
          break;
      }
    } else if (likeInput && likeInput === -1) {
      switch (tempLikeStatus) {
        case 1:
          likeStatusNumber = -1;
          break;
        case -1:
          likeStatusNumber = 0;
          break;
        default:
          likeStatusNumber = -1;
          break;
      }
    }

    if (indexInput !== null && indexInput !== undefined && indexInput > -1) {
      try {
        this.conversation.sentences[indexInput].like = likeStatusNumber;
      } catch (e) {
        Vue.set(this.conversation?.sentences?.[indexInput], 'like', likeStatusNumber);
      }

      Vue.set(this.conversation?.sentences?.[indexInput], 'like', likeStatusNumber);
    } else {
      return;
    }

    const conversationId = this.conversation.conversationId;
    const messageId = sentence.messageId;
    const messageInfoRequest = new AskMessageInfoRequest(conversationId, messageId, likeStatusNumber);

    this.buttonLikeOrDisLikeKey = new Date().getTime().toString();

    this.askGooverService().userInteraction(messageInfoRequest).then().catch();
  }

  initCompanyBriefingWelcome() {
    this.resetChat();

    const requestBody = {
      requestId: 'CPW-' + Date.now().toString(),
      conversationId: '',
      stockCode: this.stockCode || '',
      language: this.$store.getters?.userIdentity?.langKey.toUpperCase() || 'en'.toUpperCase(),
      message: '',
    };
    this.askGooverService()
      .getCompanyWelcome(requestBody)
      .then(res => {
        if (res?.data) {
          this.addToConversationSentences(
            new Sentence(CONVERSATION_ROLE.ASSISTANT, res.data, moment(new Date()).format('YYYYMMDD'), undefined, undefined, true)
          );
        }
      })
      .finally(() => {
        this.setIsLoadingBriefingWelcome(false);
      });
    if (this.getIsOpenFromMyAskGoover) {
      const conversationId = this.tempUser.id + ':' + this.stockCode;
      console.log('initCompanyBriefingWelcome', conversationId);
      this.initConversationHistoryTarget(conversationId);
      this.setIsOpenFromMyAskGoover(false);
    }
  }

  initCompanyBriefingWidgetSummarize() {
    this.resetChat();

    const requestBody = {
      conversationId: '',
      language: this.$store.getters?.userIdentity?.langKey.toUpperCase() || 'en'.toUpperCase(),
      content: this.referenceDocuments?.[0]?.content || '',
    };
    this.askGooverService()
      .getCompanySummarize(requestBody)
      .then(res => {
        if (res?.data) {
          this.addToConversationSentences(
            new Sentence(CONVERSATION_ROLE.ASSISTANT, res.data, moment(new Date()).format('YYYYMMDD'), undefined, undefined, true)
          );
        }
      });
  }

  getResourceIdsFromPage(currentPage: string) {
    switch (currentPage) {
      case 'detail':
        return [this?.getSelectedResource?.docId];
      case 'report':
        return this.referenceDocuments;
      default:
        return this.getReferenceDocuments.map(document => document.docId || document.parentDocId || document.doc_id);
    }
  }

  isCopied(indexSentence: number) {
    return this.listCopiedAnswerIndex.some(copiedAnswerIndex => Number(copiedAnswerIndex) === Number(indexSentence));
  }

  buildRequestBody() {
    let tempUserDocsList = [];
    let tempSelectedDocs = [];

    if (this.$route.name === 'Home' || this.$route.name === 'MyBriefing-Detail') {
      tempUserDocsList = this.referenceDocuments
        ?.map(item => item?.parentDocId || item?.parent_doc_id?.[0] || null)
        ?.filter(item => {
          const isValidItem = item !== null && item !== '';
          const isNotInRagDocs = !this.ragDocumentsIds?.includes(item);
          const isNotInResourceIds = !this.conversation?.sentences?.some(sentence =>
            sentence?.resourceIds?.some(resourceItem => resourceItem === item || resourceItem?.doc_id === item)
          );

          return isValidItem && isNotInRagDocs && isNotInResourceIds;
        });

      if (this.getSelectedReferenceIds?.length > 0) {
        tempSelectedDocs = [...this.getSelectedReferenceIds];
      }
    }

    let requestBody;
    switch (this.page) {
      case PAGES.COMPANY_BRIEFING:
        requestBody = {
          page: PAGES.COMPANY_BRIEFING,
          stockCode: this.stockCode || '',
        };
        break;
      case PAGES.COMPANY_BRIEFING_WIDGET:
        requestBody = {
          page: PAGES.COMPANY_BRIEFING,
          ragList: this.referenceDocuments,
        };
        break;
      default:
        requestBody = {
          page: this.page != PAGES.COMPANY_BRIEFING_WIDGET ? this.page : PAGES.COMPANY_BRIEFING,
          resourceIds: this.getResourceIdsFromPage(this.page),
          briefingId: this.targetBpId || '',
          newChat: undefined,
          userDocs: tempUserDocsList || [],
          userTime: moment(new Date()).format('YYYY-MM-DDTHH:mm:ssZ'),
          selectedDocs: tempSelectedDocs || [],
        };
        break;
    }
    return requestBody;
  }

  handleCheckIsGuestAccount(event?: any) {
    if (checkIsGuestAccount(true)) {
      event?.stopPropagation();
      event?.preventDefault();

      return;
    }
    this.openSelectTypeReference = true;
  }

  handleOpenPopupAddReferenceBySelectedReferenceTab(selectedReferenceTabInput: string) {
    if (selectedReferenceTabInput === REFERENCE_TAB_SELECT.MY_SCRAP) {
      if (checkIsGuestAccount(true)) {
        return;
      }
    }
    if (selectedReferenceTabInput) {
      this.setSelectedReferenceTab(selectedReferenceTabInput);
    }

    setTimeout(() => {
      this.setShowAddReference(true);
      this.openSelectTypeReference = false;
    }, 1);
  }

  async getListContentDetailByIds(parentDocIds: string[], requestId: string) {
    await this.briefingService()
      .callAPIGetListContentDetailByIds(parentDocIds, requestId)
      .then(response => {
        if (response?.data?.body?.body?.length > 0) {
          const tempList = response?.data?.body?.body.map(item => item?.data);

          if (tempList?.length > 0) {
            this.addToReferenceDocumentsForHistory(tempList);
          }
        }
      })
      .catch(error => {
        console.log('Error getListContentDetailByIds for chat history: ', error);
      });
  }

  addToReferenceDocumentsForHistory(resources: any[]) {
    if (!resources.length || this.page === 'detail' || this.page === 'report' || this.$route.name === 'SocialBriefing-Detail') {
      return;
    }

    let lastIndex = this.referenceDocuments.length
      ? this.referenceDocuments[this.referenceDocuments.length - 1]?.referenceDocumentIndex
      : 0;

    let maxReferenceDocumentIndex = this.referenceDocuments
      ? Math.max(...this.referenceDocuments?.map(item => item.referenceDocumentIndex))
      : 0 || 0;

    maxReferenceDocumentIndex = maxReferenceDocumentIndex > -1 ? maxReferenceDocumentIndex : 0;

    const newResource = resources.filter(resource => !this.checkDuplicateResourceForHistory(resource));

    newResource.forEach(resource => {
      lastIndex++;
      maxReferenceDocumentIndex++;
      Object.assign(resource, {
        referenceDocumentIndex: maxReferenceDocumentIndex ? maxReferenceDocumentIndex : lastIndex,
        requestId: 'Ask-' + Date.now().toString(),
        parentDocId: resource?._id ? resource?._id : '',
        parent_doc_id: resource?._id ? [resource?._id] : [],
        language: this.$store.getters?.userIdentity?.langKey.toLowerCase() || 'en',
        userKey: this.$store.getters?.userIdentity?.id ? this.$store.getters?.userIdentity?.id : '',
        requestedResourceList: newResource.map(resource => resource._id),
        forcedClustering: false,
        targetBpId: this.targetBpId ? this.targetBpId : '',
        path: resource?.source_uri ? resource?.source_uri : '',
        type: 'WEB',
        searchType: resource?.search_type ? resource?.search_type?.[0] || 'WEB' : 'WEB',
        docId: resource?._id || '',
        writer: resource?.writer
          ? resource?.writer
          : resource?.source_uri
          ? [getHostName(resource?.source_uri) ? getHostName(resource?.source_uri) : '']
          : [''],
        sourceUriDomain: resource?.source_uri_domain
          ? resource?.source_uri_domain
          : resource?.source_uri
          ? new URL(resource?.source_uri).host
          : '',
        description: resource?.content ? resource?.content : '',
        favicon: resource?.favicon_uri ? resource?.favicon_uri : '',
        keyword: '',
        originDate: resource.published_at ? resource.published_at : '',
        image_uri: resource?.image_uri ? resource.image_uri : [''],
        image_uri_thumbnail: resource?.image_uri_thumbnail ? resource?.image_uri_thumbnail : '',
        mediaType: resource?.media_type ? resource?.media_type : ['WEB'],
        docType: 1,
        isInsideGroup: false,
        isGroup: false,
        published_at: resource.published_at ? resource.published_at : '',
        service_html: resource?.service_html ? resource.service_html : '',
        chunkText: resource?.content ? resource?.content : '',
        owner_user_key: resource?.owner_user_key
          ? resource?.owner_user_key
          : resource?.originParentDocDetail?.owner_user_key
          ? resource?.originParentDocDetail?.owner_user_key
          : null,
        channel: resource?.source_channel ? resource?.source_channel : resource?.source_uri ? getHostName(resource?.source_uri) : '',
        title: resource?.title ? resource?.title : '',
      });
    });

    // add parameter isUpdate = true in briefing detail
    if (this.$route.name === 'MyBriefing-Detail' || (this.page !== undefined && this.page === PAGES.BRIEFING)) {
      this.briefingService().saveBriefingResource(newResource, true);
    } else {
      this.briefingService().saveBriefingResource(newResource);
    }

    this.addToReferentDocuments(newResource);
    this.setReferenceDocuments(newResource);
    // this.referenceDocuments = newResource;

    const tempList: any[] = newResource || [];

    if (tempList && tempList.length > 0) {
      if (this.ragDocumentsIds && this.ragDocumentsIds.length > 0) {
        this.ragDocumentsIds = [
          ...this.ragDocumentsIds,
          ...tempList
            .map(item =>
              item.parentDocId
                ? item.parentDocId
                : item?.parent_doc_id
                ? item?.parent_doc_id[0]
                : item.doc_id
                ? item.docId
                : item?._id
                ? item?._id
                : ''
            )
            .filter(item => item !== ''),
        ];
      } else {
        this.ragDocumentsIds = tempList
          .map(item =>
            item.parentDocId
              ? item.parentDocId
              : item?.parent_doc_id
              ? item?.parent_doc_id[0]
              : item.doc_id
              ? item.docId
              : item?._id
              ? item?._id
              : ''
          )
          .filter(item => item !== '');
      }
    }
  }

  checkDuplicateResourceForHistory(resource) {
    return this.referenceDocuments.some(
      referenceDocument => referenceDocument.path === resource.source_uri || referenceDocument.parentDocId === resource._id
    );
  }

  makeDataPopover(refIdInput?: number, resourcesInput?: any[]) {
    const popoverReference: PopoverReference = {
      docId: '',
      idElement: '',
      description: '',
      published_at: '',
      title: '',
      channel: '',
      url: '',
      originIndex: '',
    };

    let tempReference;
    if (refIdInput?.toString()?.trim()?.length > 0) {
      tempReference = resourcesInput?.find(item => item?.ref_idx?.toString() === refIdInput?.toString());
      if (!tempReference) {
        tempReference = resourcesInput[refIdInput - 1];
      }
      if (tempReference) {
        popoverReference.docId = tempReference?.doc_id || tempReference.docId || '';
        popoverReference.idElement = popoverReference?.docId?.concat(uuidv4().toString()) || '';
        popoverReference.description = tempReference?.content || '';

        const tempDate = tempReference?.published_at || tempReference?.create_date || '';
        popoverReference.published_at = tempDate?.trim()?.length > 0 ? formatDate(tempDate) : '';

        popoverReference.title = tempReference?.title || '';
        popoverReference.url = tempReference?.url || '';
        popoverReference.originIndex = refIdInput.toString() || '';

        if (tempReference?.url?.trim()?.length > 0) {
          try {
            const parsedUrl = new URL(tempReference?.url);

            popoverReference.channel = parsedUrl.hostname;
          } catch (e) {
            popoverReference.channel = tempReference?.url;
          }
        }

        this.popoverDataList.push(popoverReference);
      }
    }

    if (!tempReference) {
      return {
        idElement: '',
        url: resourcesInput[refIdInput - 1]?.url || '',
        numRef: refIdInput,
      };
    }
    const itemInRef = this.referenceDocuments.find(
      item => (item.docId ? item.docId : item.doc_id) === (tempReference.doc_id || tempReference.docId)
    );

    return {
      idElement: popoverReference.idElement || '',
      url: tempReference?.url || '',
      numRef: itemInRef?.referenceDocumentIndex || refIdInput,
    };
  }

  updateToTrueIndexNumber() {
    const contentString = this.conversation?.sentences?.[this.conversation?.sentences?.length - 1]?.content;

    let tempContentString = '';
    let count = 0;

    if (this.popoverDataList?.length > 0) {
      this.popoverDataList.forEach(item => {
        const originIndex = item.originIndex || '';

        const buttonElementString = '>' + originIndex + '</button></a>';

        const tempIndex = this.referenceDocuments?.[this.indexOfResourceInBriefingReference(item?.docId)]?.referenceDocumentIndex || -1;

        if (tempIndex !== undefined && tempIndex > 0) {
          if (count === 0) {
            tempContentString = contentString?.replace(buttonElementString, '>' + tempIndex + '</button></a>');
          } else {
            tempContentString = tempContentString?.replace(buttonElementString, '>' + tempIndex + '</button></a>');
          }
        }

        count++;
      });
    }

    if (tempContentString !== undefined && tempContentString?.trim()?.length > 0) {
      Vue.set(this.conversation?.sentences?.[this.conversation?.sentences?.length - 1], 'content', tempContentString);
    }
  }

  addContentToActivity(description: string, activityId: string) {
    this.askActivities = this.askActivities.map(item => {
      if (item.id === activityId) {
        item.description += description;
      }
      return item;
    });
  }

  scrollToBottomAnswerGenerationProcessData() {
    setTimeout(() => {
      // @ts-ignore
      this.$refs['answerGenerationProcessData']?.$el?.scrollTop = this.$refs['answerGenerationProcessDataWrap']?.clientHeight + 200 || 0;
    }, 300);
  }

  async clickToReSearchType(input, event) {
    if (event) {
      event.stopPropagation();
    }
    if (input.value == 'deep') {
      if (checkIsGuestAccount(true)) return;
    }
    if (input.plan) {
      const isPaid = checkLimitFeature(PAID_FEATURE_IDX.ASK_GOOVER, true);
      if (!isPaid) return;
      if (input.plan == 'Ultra') {
        const isUltra = isUltraUser(true);
        if (isUltra) return;
      }
    }
    this.researchType = input.value;
    this.setChatSetting({ ...this.getChatSetting, researchMode: this.researchType });
  }

  putOrRemoveIndexName(data, event) {
    if (event) {
      event.stopPropagation();
    }
    if (data.value === 'BASIC') return;
    if (checkIsGuestAccount(true)) return;
    if (data.plan) {
      const isPaid = isPaidUser(true);
      if (!isPaid) return;
      if (data.plan == 'Ultra') {
        const isUltra = isUltraUser(true);
        if (isUltra) return;
      }
    }
    if (!this.indexName?.includes(data.value)) {
      this.indexName.push(data.value);
    } else {
      const index = this.indexName.indexOf(data.value);
      if (index !== -1) {
        this.indexName.splice(index, 1);
      }
    }
    this.setChatSetting({ ...this.getChatSetting, dataSources: this.indexName });
  }

  editTitle() {
    this.tempTitle = this.titleChat;
    this.isEditing = true;
    this.$nextTick(() => {
      (this.$refs.titleInput as HTMLInputElement).focus();
    });
  }

  saveTitle() {
    if (!this.titleChat || this.titleChat.trim() === '') {
      // Keep focus on the input field
      this.$nextTick(() => {
        (this.$refs.titleInput as HTMLInputElement).focus();
      });

      return;
    }
    this.askGooverService()
      .saveConversationTitle({
        conversationId: this.conversation.conversationId,
        title: this.titleChat,
      })
      .then(() => {
        if (this.$route.name == 'MyCreateAsk') {
          this.$root.$emit('update-conversation-title', this.titleChat);
        }
      });
    this.isEditing = false;
  }

  cancelEdit() {
    this.titleChat = this.tempTitle;
    this.isEditing = false;
  }

  async changeLlmType(llmModel, event) {
    if (event) {
      event.stopPropagation();
    }
    if (llmModel.plan) {
      const isPaid = isPaidUser(true);
      if (!isPaid) return;
      if (llmModel.plan == 'Ultra') {
        const isUltra = isUltraUser(true);
        if (isUltra) return;
      }
    }
    this.llmType = llmModel.value;
    this.setChatSetting({ ...this.getChatSetting, llmModel: llmModel.value });
  }

  openModal() {
    switch (this.page) {
      case PAGES.BRIEFING: {
        this.setIsShowReportSettingReportType(true);
        break;
      }
      default: {
        if (!checkIsGuestAccount()) {
          this.setIsCanShowCreateAgent(true);
          this.setIsCreateNewBriefingFromChat(true);
          this.setShowCreateBriefing(true);
          this.setIsForNewBriefing(false);
        } else {
          this.setIsShowPopupRequiredLogin(true);
        }
        break;
      }
    }
  }

  handleOpenPopupEditReference() {
    if (checkIsGuestAccount(true)) {
      return;
    }

    this.setIsOpenPopupEditReferenceForPage(true);

    setTimeout(() => {
      this.setShowEditReportRef(true);
    }, 1);
  }

  handleClickCheckedReference(documentInput: any, checkedStatus?: string) {
    if (documentInput !== undefined) {
      const tempId = documentInput?.docId || documentInput?.parentDocId || documentInput?.doc_id || '';

      if (tempId?.trim()?.length > 0) {
        if (this.checkedReferenceIds?.includes(tempId) === true) {
          this.checkedReferenceIds = this.checkedReferenceIds?.filter(item => item !== tempId);
        } else {
          this.checkedReferenceIds.push(tempId);
        }
      }
    }

    if (checkedStatus !== undefined) {
      if (checkedStatus === 'switchAll') {
        if (this.checkedReferenceIds?.length === this.getReferenceDocuments?.length && this.getReferenceDocuments?.length > 0) {
          this.checkedReferenceIds = [];
        } else {
          this.checkedReferenceIds =
            this.getReferenceDocuments
              ?.map(referenceItem => referenceItem?.docId || referenceItem?.parentDocId || referenceItem?.doc_id || '')
              .filter(item => item !== undefined && item !== '') || [];
        }
      } else if (checkedStatus === 'false') {
        this.checkedReferenceIds = [];
      }
    }

    // remove popover because bug still has popover when unselect all reference
    if (!this.checkedReferenceIds || this.checkedReferenceIds?.length === 0) {
      const elements = document.querySelectorAll('#popover-clear-all');

      if (elements) {
        elements.forEach(element => {
          // @ts-ignore
          element.style.display = 'none';
        });
      }

      const tempPopover = document.getElementById('popover-clear-all');

      if (tempPopover) {
        tempPopover.style.display = 'none';
      }
    }

    this.setSelectedReferenceIds(this.checkedReferenceIds);
  }

  async showDetail(document: any) {
    if (document.docId) {
      const docId = document.docId;
      if (docId && typeof docId === 'string') {
        if (docId.includes('report')) {
          this.setShowDetailReport(true);
        } else if (docId.includes('topic')) {
          this.setShowDetailTopic(true);
        } else {
          if (await this.checkDetailArticle(docId, document.path)) {
            this.setShowDetailArticles(true);
          } else {
            let tempPath = '';

            if (document.path.includes('https://dzh91b51qfr7r.cloudfront.net/upload')) {
              tempPath = document.path;
            } else {
              if (
                document.path.includes('UID_') &&
                document.path.includes('T') &&
                document.path.includes('Z_') &&
                document.path.includes('.')
              ) {
                tempPath = 'https://dzh91b51qfr7r.cloudfront.net/upload/'.concat(document.path);
              } else {
                tempPath = document.path;
              }
            }

            window.open(tempPath, '_blank');
            return;
          }
        }
      }
      this.setSelectedResource({
        isShowSourceReference: true,
        docId: docId,
        type: document.type,
        mediaType: document.mediaType,
        isBookmarked: document.isBookmarked,
        url: document?.path,
        originParentDocDetail: document.originParentDocDetail,
      });
    } else {
      if (document.search_type?.[0] === 'USERNOTE' || document.search_type?.[0] === 'FILE') {
        return;
      }
      window.open(document.path, '_blank');
    }
  }

  async checkDetailArticle(parentDocId: string, path: string) {
    return this.discoveryService()
      .getDetailService(parentDocId)
      .then(res => {
        const idCheck = res.data.id || parentDocId;
        if (isNullOrEmptyString(idCheck)) return false;
        if (
          idCheck.includes('pdf') ||
          idCheck.includes('docx') ||
          idCheck.includes('doc') ||
          idCheck.includes('ppt') ||
          idCheck.includes('pptx') ||
          idCheck.includes('file') ||
          idCheck.includes('usernote') ||
          path.includes('.pdf') ||
          path.includes('.docx') ||
          path.includes('.doc') ||
          path.includes('.ppt') ||
          path.includes('.pptx') ||
          path.includes('.file')
        ) {
          return true;
        }
        return !!(res?.data && res?.data?.serviceHtml);
      });
  }

  removeAnchorButtons(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Select all <a> elements
    const anchorElements = doc.querySelectorAll('a');

    anchorElements.forEach(anchor => {
      // Check if the <a> element contains a <button> with an id attribute
      const button = anchor.querySelector('button[id]');
      if (button) {
        anchor.remove(); // Remove the entire <a> element
      }
    });

    return doc.body.innerHTML;
  }
}
